import React, { createContext, FC, ReactNode, useContext } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import { SearchInput } from '../../../../../widgets/input';
import { FilterIcon } from '../../../../../widgets/icons/scene';
import { thinScrollbar } from '../../../../../widgets/scrollbar';
import SceneEditorActionBar, { SceneEditorAction } from './sceneEditorActionBar';
import { SHOW_NOT_YET_AVAILABLE } from './notYetAvailable';

// IL GUSCIO DI UNA SEZIONE, UNO SOLO PER TUTTE. Oggetti, luci, camere, materiali, texture e
// ambienti si aprono nello stesso modo: la barra dei comandi in cima, la riga di ricerca
// sotto, e il contenuto — un albero o una griglia — che prende quello che resta.
//
// Sta scritto una volta perché le tre misure che contano — l'altezza della barra, i margini
// della riga di ricerca, il rientro del contenuto — devono essere le stesse in tutte le
// sezioni: passando da una all'altra i comandi non si spostano di due pixel. Sei copie di
// queste regole sono sei occasioni di disallineamento, e si vedono tutte insieme
// commutando il rail.
//
// LA RICERCA STA QUI, non dentro l'albero. L'albero è il contenuto della sezione, e il
// campo che lo filtra appartiene alla sezione: nella griglia dei materiali l'albero non
// c'è, e la ricerca deve restare dov'era.

/**
 * DOVE STA LA SEZIONE, non chi è. Nella colonna la sezione è alta e stretta, nella striscia
 * sotto il palco è bassa e larga: cambia una cosa sola — dove va la ricerca — e la sezione
 * non deve saperne altro. Passa da un contesto e non da una prop perché fra lo Scene Editor e
 * la sezione c'è il rail, che le monta per interfaccia e non le conosce una per una.
 */
export type SectionPlacement = 'panel' | 'strip';

const SectionPlacementContext = createContext<SectionPlacement>('panel');

export const SectionPlacementProvider = SectionPlacementContext.Provider;

export const useSectionPlacement = () => useContext(SectionPlacementContext);

/* A labelled landmark, not an anonymous box: the Composer names its structure column the same way,
   and a screen reader then lands on "Scene objects" instead of on a div. */
const Panel = styled.section`
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
`;

const ToolsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
`;

const SearchField = styled.div<{ $inline?: boolean }>`
    flex: ${(props) => (props.$inline ? '0 1 220px' : '1')};
    min-width: ${(props) => (props.$inline ? '140px' : '0')};
`;

// «All» non è ancora un filtro: il dato su cui filtrare — a cosa serve un asset, dove è
// usato — non esiste (docs/scene-editor-new-sections.md § 6). Il posto però c'è, e il
// bottone dice cosa diventerà invece di sparire e far riprogettare la riga.
const FilterButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
    height: 30px;
    padding: 0 10px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 6px;
    background: ${(props) => props.theme.app.backgroundColor};
    color: ${(props) => props.theme.app.primary};
    font-size: 12px;
    cursor: not-allowed;

    svg {
        width: 14px;
        height: 14px;
    }
`;

/* The only thing that scrolls in the column, with the thin scrollbar at the panel edge: the
   container around it holds no padding and no overflow of its own. */
const Content = styled.div`
    flex: 1;
    min-height: 0;
    padding: 8px 10px 12px;
    overflow-y: auto;
    ${thinScrollbar}
`;

export interface SectionPanelProps {
    actions: SceneEditorAction[];
    /** Chiude la barra dei comandi a destra: le viste della libreria, non un comando. */
    trailing?: ReactNode;
    /** Chiude la riga di ricerca a destra: le viste della libreria stanno accanto al campo che la filtra. */
    searchTrailing?: ReactNode;
    filter: string;
    onFilterChange: (value: string) => void;
    searchPlaceholder: string;
    /**
     * Il filtro d'uso: lo chiedono le sezioni della libreria, non le liste di scena. Oggi è un
     * segnaposto spento, e si disegna solo con `SHOW_NOT_YET_AVAILABLE` acceso.
     */
    showUsageFilter?: boolean;
    /**
     * L'elenco si sceglie a mucchio, e la nota lo insegna. Sta in coda e non in cima perché è
     * una cosa che si scopre lavorando, non un'istruzione da leggere prima di cominciare.
     */
    children: ReactNode;
    /**
     * IL VUOTO SOTTO L'ELENCO È UNA RISPOSTA, e quel vuoto non è sempre dell'elenco: quando le righe
     * non arrivano in fondo, la parte scoperta appartiene a questo pannello. Senza questo, cliccare
     * più in basso non voleva dire niente — e deselezionare finiva per dipendere da quante righe ci
     * sono. Arriva solo dal vuoto vero, non da una riga che lascia passare il clic.
     */
    onEmptySpaceClick?: () => void;
    /** The name of the section, so the column is a landmark a screen reader can reach. */
    label?: string;
}

const SectionPanel: FC<SectionPanelProps> = ({
    actions,
    label,
    trailing,
    searchTrailing,
    filter,
    onFilterChange,
    searchPlaceholder,
    showUsageFilter,
    onEmptySpaceClick,
    children
}) => {
    const placement = useSectionPlacement();
    const inStrip = placement === 'strip';

    const search = (
        // Filtrare un elenco cambia ciò che si vede, non la scena: vedi `data-view-only` in SceneEditor.
        <SearchField $inline={inStrip} data-view-only='true'>
            <SearchInput
                placeholder={searchPlaceholder}
                value={filter}
                onChange={(event) => onFilterChange(event.currentTarget.value)}
                onClear={() => onFilterChange('')}
            />
        </SearchField>
    );

    const usageFilter =
        showUsageFilter && SHOW_NOT_YET_AVAILABLE ? (
            <FilterButton type='button' disabled aria-label={T._('Filter by usage — not available yet', 'Admin')}>
                <FilterIcon />
                {T._('All', 'Admin')}
            </FilterButton>
        ) : null;

    return (
        <Panel aria-label={label}>
            {/* Nella striscia la riga dei comandi è larga e ha posto: ricerca e filtro ci
                stanno insieme, e la riga sotto sparisce invece di restare vuota. Nella
                colonna no — lì la barra dei comandi deve restare identica a quella delle
                altre liste, aggiungi a sinistra e distruggi al bordo. */}
            <SceneEditorActionBar
                actions={actions}
                trailing={
                    inStrip ? (
                        <>
                            {search}
                            {usageFilter}
                            {searchTrailing}
                            {trailing}
                        </>
                    ) : (
                        trailing
                    )
                }
            />
            {!inStrip && (
                <ToolsRow>
                    {search}
                    {usageFilter}
                    {searchTrailing}
                </ToolsRow>
            )}
            <Content
                onClick={(event) => {
                    if (event.target !== event.currentTarget) return;

                    onEmptySpaceClick?.();
                }}
            >
                {children}
            </Content>
        </Panel>
    );
};

export default SectionPanel;
